import React, { useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import useModal from "hooks/useModal"

import { CardHeading, Fieldset, Modal } from "components"

import { Formik, Form, FieldArray } from "formik"

import formatDate from "utils/formatDate"
import sortBy from "lodash/sortBy"

//

const ITINERARY = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      itinerary {
        id
        title
        summary
        image {
          url
        }
        startDate
        endDate
        index
      }
    }
  }
`

const CREATE_TRIP_ITINERARY = gql`
  mutation CreateTripItinerary($input: CreateTripItineraryInput!) {
    createTripItinerary(input: $input) {
      id
    }
  }
`

const UPDATE_TRIP_ITINERARY = gql`
  mutation UpdateTripItinerary($input: UpdateTripItineraryInput!) {
    updateTripItinerary(input: $input) {
      id
    }
  }
`

const DELETE_TRIP_ITINERARY = gql`
  mutation DeleteTripItinerary($input: DeleteTripItineraryInput!) {
    deleteTripItinerary(input: $input) {
      id
    }
  }
`

const ItinararyContainer = ({ tripId }) => {
  const { data, loading, error } = useQuery(ITINERARY, {
    variables: { id: tripId },
  })

  const [createTripItinerary] = useMutation(CREATE_TRIP_ITINERARY)
  const [updateTripItinerary] = useMutation(UPDATE_TRIP_ITINERARY)
  const [deleteTripItinerary] = useMutation(DELETE_TRIP_ITINERARY)

  const handleCreate = async (itinerary) => {
    try {
      const input = { tripId, itinerary }

      await createTripItinerary({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: ITINERARY,
            variables: { id: tripId },
          })

          cache.writeQuery({
            query: ITINERARY,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                itinerary: [...existing?.trip?.itinerary, ...data?.itinerary],
              },
            },
          })
        },
      })
    } catch (error) {
      throw error
    }
  }

  const handleUpdate = async (itinerary) => {
    try {
      const input = { tripId, itinerary }
      await updateTripItinerary({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: ITINERARY,
            variables: { id: tripId },
          })

          const itinerary = existing?.trip?.itinerary
          const updateItinerary = data?.updateTripItinerary

          cache.writeQuery({
            query: ITINERARY,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                itinerary: [
                  ...itinerary.map((item) => {
                    const isUpdate = updateItinerary.find(
                      (_) => _.id === item.id,
                    )
                    return isUpdate ? isUpdate : item
                  }),
                ],
              },
            },
          })
        },
      })
    } catch (error) {
      throw error
    }
  }

  const handleDelete = async (itinerary) => {
    try {
      const input = { tripId, itinerary }
      await deleteTripItinerary({ variables: { input } })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const { isOpen: isEditing, openModal, closeModal } = useModal()

  const isEmpty = data?.trip?.itinerary?.length === 0

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {!data || isEmpty ? (
        <>
          <CardHeading title="Itinarary" />
          <ItineraryEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading title="Itinarary" ctaTitle="Edit" onClick={openModal} />
          <ItineraryList data={data?.trip?.itinerary} />
        </>
      )}

      <Modal title="Itinarary" isOpen={isEditing} onClose={closeModal}>
        <div className="max-w-screen-lg mx-auto">
          <ItineraryForm
            data={data?.trip?.itinerary}
            onClose={closeModal}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        </div>
      </Modal>
    </div>
  )
}

export default ItinararyContainer

//
const ItineraryEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16 flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          {" "}
          <p className="text-center">No Itinerary</p>
          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Itinerary
          </button>
        </>
      )}
    </div>
  )
}

const ItineraryList = ({ data }) => {
  const itinerary = sortBy(data, (_) => _?.startDate)

  return itinerary?.map((item, index) => (
    <ItineraryItem key={index} {...item} />
  ))
}

const ItineraryItem = ({ title, summary, image, startDate, endDate }) => {
  return (
    <div className="flex justify-between border-b-2 border-gray-100 last:border-b-0">
      <div className="p-4 w-3/12">
        <div className="aspect-w-16 aspect-h-9">
          {image && (
            <img className="w-full h-full object-cover" src={image?.url} />
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-wrap flex-col px-6 py-3 ">
        <p className="text-sm bg-purple-100 text-purple-800 px-2 py-1 leading-normal inline-block">
          {formatDate(startDate, "d. MMM")}
          {startDate !== endDate && ` - ${formatDate(endDate, "d. MMM yyyy")}`}
        </p>
        <p className="text-xl text-gray-900 leading-normal">{title}</p>
        <p className="text-base text-gray-700 leading-normal">{summary}</p>
      </div>
    </div>
  )
}

const ItineraryForm = ({ data, onClose, onCreate, onUpdate, onDelete }) => {
  const itineraryModel = {
    title: "test",
    summary: "dsda",
    image: null,
    startDate: "2021-01-01",
    endDate: "2021-01-04",
  }

  const initialValues = {
    itinerary: [...(data && data.length > 0 ? data : [itineraryModel])].map(
      (item) => ({
        ...item,
        startDate: formatDate(item.startDate, "yyyy-LL-dd"),
        endDate: formatDate(item.endDate, "yyyy-LL-dd"),
      }),
    ),
  }

  const onSubmit = useCallback(async (values, actions) => {
    actions.setSubmitting(true)

    try {
      //This is expensive because everytime this functions gets called update will happen
      //TODO detect if values chaged, and then update shallowDeep(inital, current)

      const initialDataIds = data?.map((_) => _?.id)
      const currentDataIds = values?.itinerary?.map((_) => _?.id)

      const itinerariesSorted = values?.itinerary?.map((_, index) => ({
        ..._,
        index,
      }))

      const itinareriesForCreate = itinerariesSorted?.filter((_) => !_?.id)

      const itinareriesForUpdate = itinerariesSorted?.filter(
        (_) => _.id && initialDataIds?.includes(_.id),
      )

      const itinareriesForDelete = data?.filter(
        (_) => _.id && !currentDataIds?.includes(_.id),
      )

      if (itinareriesForCreate.length > 0) {
        await onCreate(itinareriesForCreate)
      }

      if (itinareriesForUpdate.length > 0) {
        await onUpdate(itinareriesForUpdate)
      }

      if (itinareriesForDelete.length > 0) {
        await onDelete(itinareriesForDelete)
      }

      onClose && onClose()
    } catch (e) {
      actions.setSubmitting(false)
      console.error("UPDATE TRIP INFROMATION ERROR", e)
      // const errors = e.graphQLErrors.map(v => v)
      // setFieldError("server", errors)
    }
  })

  const formikProps = {
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit,
  }

  return (
    <Formik {...formikProps}>
      {({ values, isSubmitting }) => (
        <Form>
          <FieldArray
            name="itinerary"
            render={(arrayHelpers) => (
              <>
                {values.itinerary.map((item, index) => (
                  <FieldItinerary
                    key={index}
                    index={index}
                    {...item}
                    onDelete={() => arrayHelpers.remove(index)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => arrayHelpers.push(itineraryModel)}
                  className="btn-flat-primary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>

                  <span className="ml-2">Add</span>
                </button>
              </>
            )}
          />

          <ModalNavigation {...{ isSubmitting, onClose }} />
        </Form>
      )}
    </Formik>
  )
}

const FieldItinerary = ({ index, onDelete }) => {
  return (
    <div className="w-full">
      <header className="w-full bg-gray-100 shadow-sm flex justify-between p-4">
        <div />
        <div onClick={onDelete} className="cursor-pointer">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
              fill="#4A5568"
            />
          </svg>
        </div>
      </header>

      <div className="flex justify-between my-4">
        <div className="px-2 py-3 w-5/12 pr-2">
          <Fieldset
            component="image"
            name={`itinerary[${index}].image`}
            label="Image *"
            preload={true}
          />
        </div>

        <div className="flex-1 pl-2">
          <div className="flex justify-between ">
            <div className="px-2 py-3 flex-1">
              <Fieldset
                type="date"
                name={`itinerary[${index}].startDate`}
                label="From *"
              />
            </div>
            <div className="px-2 py-3 flex-1">
              <Fieldset
                type="date"
                name={`itinerary[${index}].endDate`}
                label="To *"
              />
            </div>
          </div>

          <div className="px-2 py-3">
            <Fieldset
              type="text"
              name={`itinerary[${index}].title`}
              label="Location *"
            />
          </div>

          <div className="px-2 py-3">
            <Fieldset
              component="textarea"
              name={`itinerary[${index}].summary`}
              label="Summary *"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const ModalNavigation = ({ isSubmitting, onClose }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Save
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}
